import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiDelete, apiGet, apiPost, apiPut } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Search, CheckCircle, RefreshCw, QrCode, KeyRound, Trash2, UserCheck, UserX, Plus } from 'lucide-react';
import { API_BASE_URL } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

const API = API_BASE_URL;

const emptyCreateForm = {
  full_name: '',
  private_email: '',
  phone: '',
  relation: '',
  guest_category: '',
  title: '',
  role: '',
  profile_description: '',
  fun_fact: '',
  directory_visible: true,
  plus_one_allowed: false,
  overnight_allowed: false,
  max_guests: 1,
  access_enabled: true,
  rsvp_status: 'pending',
  dietary_notes: '',
  comments: '',
  overnight_requested: false,
};

export default function AdminGuests() {
  const { toast } = useToast();
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [filter, setFilter] = useState('all');
  const [showQR, setShowQR] = useState(null);
  const [editingGuest, setEditingGuest] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [resetGuest, setResetGuest] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [deleteGuest, setDeleteGuest] = useState(null);
  const [deletingGuestId, setDeletingGuestId] = useState(null);
  const [accessGuest, setAccessGuest] = useState(null);
  const [accessAction, setAccessAction] = useState(null);
  const [accessUpdatingId, setAccessUpdatingId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [creatingGuest, setCreatingGuest] = useState(false);

  const load = () => {
    setLoading(true);
    apiGet('/api/guests')
      .then((r) => setGuests(r.data.guests || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleCheckin = async (gid) => {
    try {
      await apiPost(`/api/checkin/${gid}`, {});
      load();
    } catch (err) {
      toast({
        title: 'Check-in mislykkedes',
        description: err.response?.data?.detail || 'Prøv igen.',
        variant: 'destructive',
      });
    }
  };

  const openEdit = (guest) => {
    setEditingGuest(guest);
    setEditForm({
      full_name: guest.full_name || '',
      private_email: guest.display_email || guest.private_email || guest.corporate_email || '',
      phone: guest.phone || '',
      role: guest.role || '',
      title: guest.title || '',
      department: guest.department || '',
      relation: guest.relation || '',
      profile_description: guest.profile_description || '',
      fun_fact: guest.fun_fact || '',
      guest_category: guest.guest_category || '',
      directory_visible: guest.directory_visible !== false,
      plus_one_allowed: !!guest.plus_one_allowed,
      overnight_allowed: !!guest.overnight_allowed,
      max_guests: guest.max_guests || 1,
      rsvp_status: guest.rsvp ? (guest.rsvp.attending ? 'yes' : guest.rsvp.attending === false ? 'no' : 'pending') : 'pending',
      dietary_notes: guest.rsvp?.dietary_notes || '',
      comments: guest.rsvp?.comments || '',
      overnight_requested: !!guest.rsvp?.overnight_requested,
    });
  };

  const closeEdit = () => {
    setEditingGuest(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    try {
      await apiPut(`/api/guests/${editingGuest.id}`, editForm);
      toast({ title: 'Gæst gemt' });
      closeEdit();
      load();
    } catch (err) {
      toast({
        title: 'Kunne ikke gemme gæsten',
        description: err.response?.data?.detail || 'Prøv igen.',
        variant: 'destructive',
      });
    }
  };

  const openCreate = () => {
    setCreateForm({ ...emptyCreateForm });
    setShowCreate(true);
  };

  const closeCreate = () => {
    if (creatingGuest) return;
    setShowCreate(false);
    setCreateForm({ ...emptyCreateForm });
  };

  const submitCreateGuest = async (e) => {
    e.preventDefault();
    if (creatingGuest) return;
    setCreatingGuest(true);
    try {
      const payload = {
        ...createForm,
        max_guests: Number(createForm.max_guests || 1),
      };
      const res = await apiPost('/api/admin/guests', payload);
      if (res.data.guest) {
        setGuests((current) => [res.data.guest, ...current.filter((guest) => guest.id !== res.data.guest.id)]);
      }
      toast({ title: 'Gæsten er oprettet' });
      setShowCreate(false);
      setCreateForm({ ...emptyCreateForm });
      load();
    } catch (err) {
      toast({
        title: 'Kunne ikke oprette gæsten',
        description: err.response?.data?.detail || 'Prøv igen.',
        variant: 'destructive',
      });
    } finally {
      setCreatingGuest(false);
    }
  };

  const openAccessDialog = (guest, action) => {
    setAccessGuest(guest);
    setAccessAction(action);
  };

  const closeAccessDialog = () => {
    if (accessUpdatingId) return;
    setAccessGuest(null);
    setAccessAction(null);
  };

  const confirmAccessChange = async () => {
    if (!accessGuest?.id || !accessAction) return;
    const guestId = accessGuest.id;
    setAccessUpdatingId(guestId);
    try {
      const endpoint = accessAction === 'deactivate'
        ? `/api/admin/guests/${guestId}/deactivate-access`
        : `/api/admin/guests/${guestId}/reactivate-access`;
      const res = await apiPost(endpoint, {});
      setGuests((current) => current.map((guest) => (guest.id === guestId ? { ...guest, ...res.data.guest } : guest)));
      toast({ title: accessAction === 'deactivate' ? 'Gæsteadgang er deaktiveret' : 'Gæsteadgang er genaktiveret' });
      setAccessGuest(null);
      setAccessAction(null);
      load();
    } catch (err) {
      toast({
        title: accessAction === 'deactivate' ? 'Kunne ikke deaktivere gæsteadgang' : 'Kunne ikke genaktivere gæsteadgang',
        description: err.response?.data?.detail || 'Prøv igen.',
        variant: 'destructive',
      });
    } finally {
      setAccessUpdatingId(null);
    }
  };

  const openResetPassword = (guest) => {
    setResetGuest(guest);
    setResetPassword('');
  };

  const closeResetPassword = () => {
    if (resettingPassword) return;
    setResetGuest(null);
    setResetPassword('');
  };

  const submitResetPassword = async (e) => {
    e.preventDefault();
    if (!resetGuest) return;
    if (resetPassword.length < 6) {
      toast({
        title: 'Adgangskoden er for kort',
        description: 'Den nye adgangskode skal være mindst 6 tegn.',
        variant: 'destructive',
      });
      return;
    }

    setResettingPassword(true);
    try {
      await apiPost(`/api/admin/guests/${resetGuest.id}/reset-password`, { new_password: resetPassword });
      toast({
        title: 'Adgangskode nulstillet',
        description: `Ny adgangskode er gemt for ${resetGuest.full_name || 'gasten'}.`,
      });
      setResetGuest(null);
      setResetPassword('');
    } catch (err) {
      toast({
        title: 'Kunne ikke nulstille adgangskode',
        description: err.response?.data?.detail || 'Prøv igen.',
        variant: 'destructive',
      });
    } finally {
      setResettingPassword(false);
    }
  };

  const closeDeleteGuest = () => {
    if (deletingGuestId) return;
    setDeleteGuest(null);
  };

  const confirmDeleteGuest = async () => {
    if (!deleteGuest?.id || deletingGuestId === deleteGuest.id) return;
    const guestId = deleteGuest.id;
    setDeletingGuestId(guestId);
    try {
      await apiDelete(`/api/admin/guests/${guestId}`);
      setGuests((current) => current.filter((guest) => guest.id !== guestId));
      setShowQR((current) => (current === guestId ? null : current));
      setDeleteGuest(null);
      toast({ title: 'Gæsten er slettet' });
      load();
    } catch (err) {
      toast({
        title: 'Kunne ikke slette gæsten',
        description: err.response?.data?.detail || 'Prøv igen.',
        variant: 'destructive',
      });
    } finally {
      setDeletingGuestId(null);
    }
  };

  const filtered = guests
    .filter((g) => {
      if (filter === 'checked_in' && g.checkin_status !== 'checked_in') return false;
      if (filter === 'not_checked_in' && g.checkin_status === 'checked_in') return false;
      if (filter === 'rsvp_attending' && g.rsvp?.attending !== true) return false;
      if (filter === 'rsvp_declined' && g.rsvp?.attending !== false) return false;
      if (filter === 'no_rsvp' && g.rsvp?.attending !== null && g.rsvp?.attending !== undefined) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        g.full_name?.toLowerCase().includes(s) ||
        g.display_email?.toLowerCase().includes(s) ||
        g.corporate_email?.toLowerCase().includes(s) ||
        g.private_email?.toLowerCase().includes(s)
      );
    })
    .sort((a, b) => {
      if (sortBy === 'name') return (a.full_name || '').localeCompare(b.full_name || '');
      if (sortBy === 'email') return (a.display_email || a.private_email || a.corporate_email || '').localeCompare(b.display_email || b.private_email || b.corporate_email || '');
      if (sortBy === 'checkin') return (a.checkin_status === 'checked_in' ? -1 : 1) - (b.checkin_status === 'checked_in' ? -1 : 1);
      if (sortBy === 'rsvp') return (a.rsvp ? -1 : 1) - (b.rsvp ? -1 : 1);
      return 0;
    });

  return (
    <div className="animate-fade-in" data-testid="admin-guests-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-50" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Gæster
          </h1>
          <p className="text-sm text-slate-400 mt-1">{guests.length} registreret</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={openCreate} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white">
            <Plus className="w-4 h-4" />
            Opret gæst
          </Button>
          <Button variant="outline" onClick={load} className="w-full sm:w-auto bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4 sm:flex-wrap">
        <div className="relative w-full sm:flex-1 sm:min-w-[16rem] sm:max-w-sm">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Søg efter navn eller e-mail..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md pl-9 pr-3 py-2 outline-none text-sm"
          />
        </div>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="w-full sm:w-auto bg-slate-900/50 border border-slate-700 text-slate-300 rounded-md px-3 py-2 text-sm outline-none"
        >
          <option value="name">Sortér: Navn</option>
          <option value="email">Sortér: E-mail</option>
          <option value="checkin">Sortér: Check-in</option>
          <option value="rsvp">Sortér: RSVP</option>
        </select>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full sm:w-auto bg-slate-900/50 border border-slate-700 text-slate-300 rounded-md px-3 py-2 text-sm outline-none"
        >
          <option value="all">Filter: Alle</option>
          <option value="checked_in">Checket ind</option>
          <option value="not_checked_in">Ikke checket ind</option>
          <option value="rsvp_attending">RSVP deltager</option>
          <option value="rsvp_declined">RSVP deltager ikke</option>
          <option value="no_rsvp">Intet RSVP</option>
        </select>
        <Button variant="outline" onClick={() => window.print()} className="w-full sm:w-auto bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700">
          Udskriv
        </Button>
      </div>

      {loading ? (
        <p className="text-slate-400">Indlæser...</p>
      ) : (
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Gæst</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">E-mail</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">RSVP</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Check-in</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Adgang</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Oplysninger</th>
                  <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Handlinger</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((g) => (
                  <tr key={g.id} className="border-b border-slate-700/20 hover:bg-slate-800/30">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 flex items-center justify-center text-xs text-blue-300">
                          {g.full_name?.charAt(0)}
                        </div>
                        <div>
                          <span className="text-slate-200">{g.full_name}</span>
                          {g.title && <p className="text-xs text-slate-500">{g.title}</p>}
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3 text-xs font-mono text-blue-400">{g.display_email || g.private_email || g.corporate_email}</td>

                    <td className="px-4 py-3">
                      {g.rsvp && (g.rsvp.attending === true || g.rsvp.attending === false) ? (
                        <span className={`text-xs px-2 py-1 rounded ${g.rsvp.attending ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                          {g.rsvp.attending ? `Ja (${g.rsvp.guest_count})` : 'Nej'}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500">Ikke svaret</span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      {g.checkin_status === 'checked_in' ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-emerald-500/10 text-emerald-400">
                          <CheckCircle className="w-3 h-3" /> Ankommet
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500">Ikke ankommet</span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      {g.access_enabled === false ? (
                        <span className="text-xs px-2 py-1 rounded bg-red-500/10 text-red-300">Deaktiveret</span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded bg-emerald-500/10 text-emerald-300">Aktiv</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-xs text-slate-400 space-x-1">
                      {g.role && <span className="px-1.5 py-0.5 bg-purple-500/10 text-purple-400 rounded">{g.role}</span>}
                      {g.department && <span className="px-1.5 py-0.5 bg-indigo-500/10 text-indigo-400 rounded">{g.department}</span>}
                      {g.overnight_allowed && <span className="px-1.5 py-0.5 bg-teal-500/10 text-teal-400 rounded">Overnatning</span>}
                      {g.plus_one_allowed && <span className="px-1.5 py-0.5 bg-blue-500/10 text-blue-400 rounded">+1</span>}
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(g)} className="min-h-9 min-w-9 rounded hover:bg-blue-500/10 text-slate-400 hover:text-blue-400" title="Rediger gæst">
                          ✏️
                        </button>

                        <button onClick={() => setShowQR(showQR === g.id ? null : g.id)} className="min-h-9 min-w-9 rounded hover:bg-slate-700 text-slate-400 hover:text-blue-400" title="QR-kode">
                          <QrCode className="w-3.5 h-3.5" />
                        </button>

                        <button onClick={() => openResetPassword(g)} className="min-h-9 min-w-9 rounded hover:bg-amber-500/10 text-slate-400 hover:text-amber-400" title="Nulstil adgangskode">
                          <KeyRound className="w-3.5 h-3.5" />
                        </button>

                        {g.rsvp?.attending === false && (
                          <button
                            onClick={() => openAccessDialog(g, g.access_enabled === false ? 'reactivate' : 'deactivate')}
                            disabled={accessUpdatingId === g.id}
                            className={`min-h-9 min-w-9 rounded disabled:opacity-40 ${
                              g.access_enabled === false
                                ? 'text-slate-400 hover:bg-emerald-500/10 hover:text-emerald-400'
                                : 'text-slate-400 hover:bg-red-500/10 hover:text-red-400'
                            }`}
                            title={g.access_enabled === false ? 'Genaktivér gæsteadgang' : 'Deaktivér gæsteadgang'}
                          >
                            {g.access_enabled === false ? <UserCheck className="w-3.5 h-3.5" /> : <UserX className="w-3.5 h-3.5" />}
                          </button>
                        )}

                        <button
                          onClick={() => setDeleteGuest(g)}
                          disabled={deletingGuestId === g.id}
                          className="min-h-9 min-w-9 rounded hover:bg-red-500/10 text-slate-400 hover:text-red-400 disabled:opacity-40"
                          title="Slet gæst"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>

                        {g.checkin_status !== 'checked_in' && (
                          <button onClick={() => handleCheckin(g.id)} className="min-h-9 min-w-9 rounded hover:bg-emerald-500/10 text-slate-400 hover:text-emerald-400" title="Check ind">
                            <CheckCircle className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>

                      {showQR === g.id && (
                        <div className="mt-2 inline-block">
                          <img src={`${API}/api/guests/${g.id}/qr`} alt="QR" className="w-32 h-32 rounded border border-slate-700" />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filtered.length === 0 && <p className="text-center py-8 text-slate-500 text-sm">Ingen gæster fundet</p>}
        </div>
      )}

      {showCreate &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center p-4"
            style={{ zIndex: 9999 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeCreate();
            }}
          >
            <form onSubmit={submitCreateGuest} className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-5 sm:p-6 shadow-2xl">
              <h2 className="text-xl font-semibold text-slate-50 mb-1">Opret gæst</h2>
              <p className="text-xs text-slate-500 mb-5">Gæsten oprettes med en invitationskode, men der sendes ikke automatisk mail.</p>

              <div className="space-y-4">
                <input value={createForm.full_name} onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })} placeholder="Fulde navn *" className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100" required autoFocus />
                <input type="email" value={createForm.private_email} onChange={(e) => setCreateForm({ ...createForm, private_email: e.target.value })} placeholder="Privat e-mail" className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100" />
                <input value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} placeholder="Telefonnummer" className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100" />

                <div className="grid gap-3 sm:grid-cols-2">
                  <input value={createForm.relation} onChange={(e) => setCreateForm({ ...createForm, relation: e.target.value })} placeholder="Relation/kategori" className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100" />
                  <input value={createForm.guest_category} onChange={(e) => setCreateForm({ ...createForm, guest_category: e.target.value })} placeholder="Kategori" className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100" />
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <input value={createForm.title} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} placeholder="Titel / rolle" className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100" />
                  <input value={createForm.role} onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })} placeholder="Rolle" className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100" />
                </div>

                <div className="border-t border-slate-800 pt-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Offentlig profil</p>
                  <div className="space-y-3">
                    <textarea value={createForm.profile_description} onChange={(e) => setCreateForm({ ...createForm, profile_description: e.target.value })} placeholder="Kort profiltekst" rows={3} className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 resize-none" />
                    <textarea value={createForm.fun_fact} onChange={(e) => setCreateForm({ ...createForm, fun_fact: e.target.value })} placeholder="Fun fact" rows={2} className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 resize-none" />
                    <label className="flex items-center gap-2 text-sm text-slate-300">
                      <input type="checkbox" checked={createForm.directory_visible} onChange={(e) => setCreateForm({ ...createForm, directory_visible: e.target.checked })} />
                      Vis i Mød gæsterne
                    </label>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-[1fr_10rem]">
                  <select value={createForm.rsvp_status} onChange={(e) => setCreateForm({ ...createForm, rsvp_status: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100">
                    <option value="pending">RSVP: Ikke svaret</option>
                    <option value="yes">RSVP: Ja</option>
                    <option value="no">RSVP: Nej</option>
                  </select>
                  <input type="number" min="1" max="20" value={createForm.max_guests} onChange={(e) => setCreateForm({ ...createForm, max_guests: e.target.value })} placeholder="Antal" className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100" />
                </div>

                <div className="flex flex-col gap-2 text-sm text-slate-300">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={createForm.plus_one_allowed} onChange={(e) => setCreateForm({ ...createForm, plus_one_allowed: e.target.checked })} />
                    Plus-one tilladt
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={createForm.overnight_allowed} onChange={(e) => setCreateForm({ ...createForm, overnight_allowed: e.target.checked, overnight_requested: e.target.checked ? createForm.overnight_requested : false })} />
                    Overnatning tilladt
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={createForm.access_enabled} onChange={(e) => setCreateForm({ ...createForm, access_enabled: e.target.checked })} />
                    Gæsteadgang aktiv
                  </label>
                </div>

                <div className="border-t border-slate-800 pt-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">RSVP og kost</p>
                  <div className="space-y-3">
                    <textarea value={createForm.dietary_notes} onChange={(e) => setCreateForm({ ...createForm, dietary_notes: e.target.value })} placeholder="Kosthensyn og allergier" rows={3} className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 resize-none" />
                    <textarea value={createForm.comments} onChange={(e) => setCreateForm({ ...createForm, comments: e.target.value })} placeholder="RSVP-kommentar" rows={2} className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 resize-none" />
                    <label className="flex items-center gap-2 text-sm text-slate-300">
                      <input type="checkbox" checked={createForm.overnight_requested} disabled={!createForm.overnight_allowed} onChange={(e) => setCreateForm({ ...createForm, overnight_requested: e.target.checked })} />
                      Overnatning ønsket
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-6 sticky bottom-0 bg-slate-900 pt-4">
                <Button type="button" variant="outline" onClick={closeCreate} disabled={creatingGuest} className="w-full sm:w-auto bg-slate-800 border-slate-700 text-slate-300">
                  Annuller
                </Button>
                <Button type="submit" disabled={creatingGuest} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50">
                  {creatingGuest ? 'Opretter...' : 'Opret gæst'}
                </Button>
              </div>
            </form>
          </div>,
          document.body
        )}

      {editingGuest &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center p-4"
            style={{ zIndex: 9999 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeEdit();
            }}
          >
            <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-5 sm:p-6 shadow-2xl">
              <h2 className="text-xl font-semibold text-slate-50 mb-1">Rediger gæst</h2>
              <p className="text-xs text-slate-500 mb-5">{editingGuest.display_email || editingGuest.private_email || editingGuest.corporate_email}</p>

              <div className="space-y-4">
                <input
                  value={editForm.full_name}
                  onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                  placeholder="Fulde navn"
                  className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
                />

                <input
                  type="email"
                  value={editForm.private_email}
                  onChange={(e) => setEditForm({ ...editForm, private_email: e.target.value })}
                  placeholder="E-mail"
                  className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
                />

                <input
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="Telefon"
                  className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
                />

                <input
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  placeholder="Titel / jobtitel"
                  className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
                />

                <input
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  placeholder="Rolle"
                  className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
                />

                <input
                  value={editForm.department}
                  onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                  placeholder="Afdeling"
                  className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
                />

                <div className="border-t border-slate-800 pt-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Mød gæsterne</p>
                  <div className="space-y-3">
                    <input
                      value={editForm.relation}
                      onChange={(e) => setEditForm({ ...editForm, relation: e.target.value })}
                      placeholder="Relation til Thomas"
                      className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
                    />
                    <input
                      value={editForm.guest_category}
                      onChange={(e) => setEditForm({ ...editForm, guest_category: e.target.value })}
                      placeholder="Kategori"
                      className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
                    />
                    <textarea
                      value={editForm.profile_description}
                      onChange={(e) => setEditForm({ ...editForm, profile_description: e.target.value })}
                      placeholder="Kort profiltekst"
                      rows={3}
                      className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 resize-none"
                    />
                    <textarea
                      value={editForm.fun_fact}
                      onChange={(e) => setEditForm({ ...editForm, fun_fact: e.target.value })}
                      placeholder="Fun fact"
                      rows={2}
                      className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 resize-none"
                    />
                    <label className="flex items-center gap-2 text-sm text-slate-300">
                      <input
                        type="checkbox"
                        checked={editForm.directory_visible}
                        onChange={(e) => setEditForm({ ...editForm, directory_visible: e.target.checked })}
                      />
                      Vis i Mød gæsterne
                    </label>
                  </div>
                </div>

                <input
                  type="number"
                  min="1"
                  value={editForm.max_guests}
                  onChange={(e) => setEditForm({ ...editForm, max_guests: Number(e.target.value) })}
                  placeholder="Maks. antal gæster"
                  className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
                />

                <div className="flex flex-col gap-2 text-sm text-slate-300">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editForm.plus_one_allowed}
                      onChange={(e) => setEditForm({ ...editForm, plus_one_allowed: e.target.checked })}
                    />
                    Plus-one tilladt
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editForm.overnight_allowed}
                      onChange={(e) => setEditForm({ ...editForm, overnight_allowed: e.target.checked })}
                    />
                    Overnatning tilladt
                  </label>
                </div>

                <div className="border-t border-slate-800 pt-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">RSVP og kost</p>
                  <div className="space-y-3">
                    <select
                      value={editForm.rsvp_status}
                      onChange={(e) => setEditForm({ ...editForm, rsvp_status: e.target.value })}
                      className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
                    >
                      <option value="pending">RSVP: Ikke svaret</option>
                      <option value="yes">RSVP: Ja</option>
                      <option value="no">RSVP: Nej</option>
                    </select>
                    <textarea
                      value={editForm.dietary_notes}
                      onChange={(e) => setEditForm({ ...editForm, dietary_notes: e.target.value })}
                      placeholder="Kostbehov, allergier eller andre kosthensyn"
                      rows={3}
                      className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 resize-none"
                    />
                    <textarea
                      value={editForm.comments}
                      onChange={(e) => setEditForm({ ...editForm, comments: e.target.value })}
                      placeholder="RSVP-kommentar"
                      rows={2}
                      className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 resize-none"
                    />
                    <label className="flex items-center gap-2 text-sm text-slate-300">
                      <input
                        type="checkbox"
                        checked={editForm.overnight_requested}
                        onChange={(e) => setEditForm({ ...editForm, overnight_requested: e.target.checked })}
                      />
                      Overnatning ønsket
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-6 sticky bottom-0 bg-slate-900 pt-4">
                <Button variant="outline" onClick={closeEdit} className="w-full sm:w-auto bg-slate-800 border-slate-700 text-slate-300">
                  Annuller
                </Button>
                <Button onClick={saveEdit} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white">
                  Gem
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {resetGuest &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center p-4"
            style={{ zIndex: 9999 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeResetPassword();
            }}
          >
            <form onSubmit={submitResetPassword} className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md p-5 sm:p-6 shadow-2xl">
              <div className="flex items-start gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 text-amber-300 flex items-center justify-center flex-shrink-0">
                  <KeyRound className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl font-semibold text-slate-50">Nulstil adgangskode</h2>
                  <p className="text-xs text-slate-500 mt-1 break-words">{resetGuest.full_name} · {resetGuest.display_email || resetGuest.private_email || resetGuest.corporate_email}</p>
                </div>
              </div>

              <label className="block text-sm text-slate-300 mb-2" htmlFor="guest-reset-password">
                Ny adgangskode
              </label>
              <input
                id="guest-reset-password"
                type="password"
                minLength={6}
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                placeholder="Mindst 6 tegn"
                className="w-full bg-slate-950 border border-slate-700 focus:border-amber-500 rounded-md px-3 py-2 text-sm text-slate-100 outline-none"
                autoFocus
                required
              />
              <p className="text-xs text-slate-500 mt-2">Den nuværende adgangskode vises aldrig.</p>

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-6">
                <Button type="button" variant="outline" onClick={closeResetPassword} disabled={resettingPassword} className="w-full sm:w-auto bg-slate-800 border-slate-700 text-slate-300">
                  Annuller
                </Button>
                <Button type="submit" disabled={resettingPassword} className="w-full sm:w-auto bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50">
                  {resettingPassword ? 'Gemmer...' : 'Bekraeft'}
                </Button>
              </div>
            </form>
          </div>,
          document.body
        )}

      {deleteGuest &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center p-4"
            style={{ zIndex: 9999 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeDeleteGuest();
            }}
          >
            <div className="bg-slate-900 border border-red-500/30 rounded-xl w-full max-w-md p-5 sm:p-6 shadow-2xl">
              <div className="flex items-start gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 text-red-300 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl font-semibold text-slate-50">Slet gæst</h2>
                  <p className="text-xs text-slate-500 mt-1 break-words">
                    {deleteGuest.full_name} · {deleteGuest.display_email || deleteGuest.private_email || deleteGuest.corporate_email}
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-100/90 leading-relaxed">
                Er du sikker på, at du vil slette {deleteGuest.full_name || 'denne gæst'}? Handlingen kan ikke fortrydes.
              </div>

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-6">
                <Button type="button" variant="outline" onClick={closeDeleteGuest} disabled={!!deletingGuestId} className="w-full sm:w-auto bg-slate-800 border-slate-700 text-slate-300">
                  Annuller
                </Button>
                <Button type="button" onClick={confirmDeleteGuest} disabled={deletingGuestId === deleteGuest.id} className="w-full sm:w-auto bg-red-600 hover:bg-red-500 text-white disabled:opacity-50">
                  {deletingGuestId === deleteGuest.id ? 'Sletter...' : 'Slet gæst'}
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {accessGuest &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center p-4"
            style={{ zIndex: 9999 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeAccessDialog();
            }}
          >
            <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md p-5 sm:p-6 shadow-2xl">
              <div className="flex items-start gap-3 mb-5">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${accessAction === 'deactivate' ? 'bg-red-500/10 text-red-300' : 'bg-emerald-500/10 text-emerald-300'}`}>
                  {accessAction === 'deactivate' ? <UserX className="w-5 h-5" /> : <UserCheck className="w-5 h-5" />}
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl font-semibold text-slate-50">
                    {accessAction === 'deactivate' ? 'Deaktivér gæsteadgang' : 'Genaktivér gæsteadgang'}
                  </h2>
                  <p className="text-xs text-slate-500 mt-1 break-words">
                    {accessGuest.full_name} · {accessGuest.display_email || accessGuest.private_email || accessGuest.corporate_email}
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-slate-700 bg-slate-950/60 p-4 text-sm text-slate-200 leading-relaxed">
                {accessAction === 'deactivate'
                  ? 'Gæsten beholder sin RSVP-historik, men kan ikke længere logge ind på Molin30.'
                  : 'Gæsten får adgang til Molin30 igen. RSVP-historikken ændres ikke.'}
              </div>

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-6">
                <Button type="button" variant="outline" onClick={closeAccessDialog} disabled={!!accessUpdatingId} className="w-full sm:w-auto bg-slate-800 border-slate-700 text-slate-300">
                  Annuller
                </Button>
                <Button
                  type="button"
                  onClick={confirmAccessChange}
                  disabled={accessUpdatingId === accessGuest.id}
                  className={`w-full sm:w-auto text-white disabled:opacity-50 ${accessAction === 'deactivate' ? 'bg-red-600 hover:bg-red-500' : 'bg-emerald-600 hover:bg-emerald-500'}`}
                >
                  {accessUpdatingId === accessGuest.id ? 'Gemmer...' : accessAction === 'deactivate' ? 'Deaktivér adgang' : 'Genaktivér adgang'}
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
